/* eslint-disable max-nested-callbacks */
import { Grid } from '@mui/material'
import Card from 'components/card/Card'
import PropTypes from 'prop-types'
import Tabs from 'components/Tabs'
import { exportPictureIcon } from 'components/echart/EChartUtils'
import Input from 'components/forms/Input'
import Textarea from 'components/forms/Textarea'
import Icon from 'components/icon/Icon'
import ReactEcharts from 'echarts-for-react'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import useBoolean from 'utils/customHook/useBoolean'
import { getDate } from 'utils/DateUtil'
import { normalizeString } from 'utils/StringUtil'
import { difference, keys, orderBy, round, sum, uniq, uniqBy, compact } from 'lodash'
import useActions from 'utils/customHook/useActions'
import { NewTable, WrapperBox } from 'components/datatable/NewTable'
import ConfirmModal from 'components/modal/ConfirmModal'
import HydrobioAction from 'quality/actions/HydrobioAction'
import OperationAction from 'quality/components/operation/actions/OperationAction'
import { HydrobioFloristicDialog } from './HydrobioListDialog'
import HydrobioListTools from './HydrobioListTools'
import { SUPPORT_DIATOMS, SUPPORT_MACROPHYTES } from '../../../constants/HydrobioConstant'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { THRESHOLD_COLORS_CODES, whiteColor } from 'utils/constants/ColorTheme'

const TABLE = 'TABLE'
const GRAPH = 'GRAPH'

const TablePanel = () => {
    const dispatch = useDispatch()

    const showOmnidiaCode = useAccountSetting('showOmnidiaCode', d => d === '1')
    const showMacrophyteCode = useAccountSetting('viewMacrophyteCode', d => d === '1')

    const {
        hydrobioList,
        taxons,
        accountUser,
        hydrobioOperation,
        remarks,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        taxons: store.TaxonReducer.taxons,
        accountUser: store.AccountReducer.accountUser,
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        remarks: store.OperationReducer.remarks,
    }), shallowEqual)

    const {
        value: isOpenConfirmDelete,
        setFalse: onCloseDelete,
        setTrue: onOpenDelete,
    } = useBoolean(false)
    const {
        value: isEditModalOpen,
        setFalse: onCloseEditModal,
        setTrue: onOpenEditModal,
    } = useBoolean(false)
    const [selectedHydrobioListId, setSelectedHydrobioListId] = useState()

    const [searchValue, setSearchValue] = useState('')

    const sumTotal = sum(hydrobioList.map(({ enumerationA = 0, enumerationB = 0, enumerationC = 0, enumerationC2 = 0 }) => enumerationA + enumerationB + enumerationC + enumerationC2))
    const sumPercentTotal = sum(hydrobioList.map(({ percentageA = 0, percentageB = 0 }) => percentageA + percentageB))
    const isUR = hydrobioOperation.support === SUPPORT_MACROPHYTES

    const listFormated = useMemo(() => {
        return hydrobioList.map(list => {
            const {
                id,
                taxon: taxonCode,
                enumerationA = 0,
                enumerationB = 0,
                enumerationC = 0,
                enumerationC2 = 0,
            } = list
            const taxon = taxons.find(t => t.code === taxonCode)
            const percentageA = list.percentageA ?? 0
            const percentageB = list.percentageB ?? 0
            const sumEnumeration = enumerationA + enumerationB + enumerationC + enumerationC2
            const sumPercent = percentageA + percentageB
            const plenty = isUR
                ? (sumPercentTotal !== 0 ? round(100 * sumPercent / sumPercentTotal, 2) : 0)
                : (sumTotal !== 0 ? round(100 * sumEnumeration / sumTotal, 2) : 0)
            return {
                id,
                taxon: taxon?.latinName || taxonCode,
                omnidiaCode: taxon?.omnidiaCode ?? taxon?.omnidiaSynonym ?? taxon?.teraOmnidia,
                macrophyteCode: taxon?.macrophyteCode,
                enumeration: sumEnumeration,
                plenty: { value: `${plenty} %`, sortValue: plenty },
                searchValue: normalizeString(`${taxon?.labelWithCode || taxonCode} ${taxon?.omnidiaCode}`),
                ur1: { value: `${percentageA} %`, sortValue: percentageA },
                ur2: { value: `${percentageB} %`, sortValue: percentageB },
                remark: list.remark && remarks.find(r => r.id === list.remark)?.name,
            }
        })
    }, [hydrobioList, taxons, sumTotal, sumPercentTotal, isUR, remarks])

    const listFiltered = useMemo(() => {
        const searchValueFormat = normalizeString(searchValue)
        return searchValueFormat ? listFormated.filter(l => l.searchValue.includes(searchValueFormat)) : listFormated
    }, [listFormated, searchValue])

    const selectedHydrobioList = hydrobioList.find(hl => hl.id === selectedHydrobioListId)

    const headers = useMemo(() => {
        if (isUR && showMacrophyteCode) {
            return ['taxon', 'macrophyteCode', { key: 'ur1', value: `${i18n.relevedUnit} 1` }, { key: 'ur2', value: `${i18n.relevedUnit} 2` }, 'plenty', 'remark']
        } else if (isUR) {
            return ['taxon', { key: 'ur1', value: `${i18n.relevedUnit} 1` }, { key: 'ur2', value: `${i18n.relevedUnit} 2` }, 'plenty', 'remark']
        } else if (hydrobioOperation.support === SUPPORT_DIATOMS && showOmnidiaCode) {
            return ['taxon', 'omnidiaCode', 'enumeration', 'plenty']
        }
        return ['taxon', 'enumeration', 'plenty']
    }, [hydrobioOperation, isUR])

    return (
        <>
            <Grid container spacing={1} style={{ padding: '10 10 5 10' }}>
                <Grid size={4}>
                    <Input
                        title={i18n.search}
                        value={searchValue}
                        onChange={setSearchValue}
                    />
                </Grid>
                {/* <Grid size={4}>
                    <ThresholdSelect
                        selected={threshold}
                        onChange={setThreshold}
                        nullLabel='&nbsp;'
                    />
                </Grid>
                <Grid size={4}>
                    <SelectionSelect
                        onChange={(results, value) => {
                            setSelection(value)
                            setSelectionResults(results)
                        }}
                    />
                </Grid> */}
            </Grid>
            <HydrobioListTools getTaxonLabel={list => taxons.find(t => t.code === list.taxon)?.labelWithCode || list.taxon} />
            <NewTable
                rows={orderBy(listFiltered, 'id')}
                headers={headers}
                rowsPerPageOptions={nbPerPageLabel}
                lineActions={[
                    {
                        icon: 'delete',
                        onClick: ({ id }) => {
                            setSelectedHydrobioListId(id)
                            onOpenDelete()
                        },
                        displayed: accountUser.consultant !== '1',
                    },
                    {
                        icon: 'edit',
                        onClick: ({ id }) => {
                            setSelectedHydrobioListId(id)
                            onOpenEditModal()
                        },
                        displayed: accountUser.consultant !== '1',
                    },
                ]}
                WrapperComponent={WrapperBox}
            />
            <ConfirmModal
                isOpen={isOpenConfirmDelete}
                title={i18n.deletingConfirmation}
                onValidate={() => {
                    const {
                        qualitometer,
                        operation,
                        id,
                    } = selectedHydrobioList
                    dispatch(HydrobioAction.deleteHydrobioList(qualitometer, operation, id))
                        .then(res => {
                            if (res?.delete > 0) {
                                dispatch(OperationAction.fetchHydrobioList(qualitometer, operation))
                                onCloseDelete()
                                setSelectedHydrobioListId()
                            }
                        })
                }}
                onClose={() => {
                    onCloseDelete()
                    setSelectedHydrobioListId()
                }}
            />
            <HydrobioFloristicDialog
                isOpen={isEditModalOpen}
                onClose={onCloseEditModal}
                onValidate={(updatedHydrobioList) => {
                    const {
                        qualitometer,
                        operation,
                        id,
                    } = updatedHydrobioList
                    dispatch(HydrobioAction.updateHydrobioList(qualitometer, operation, id, updatedHydrobioList))
                        .then(res => {
                            // les toasts de succès / d'erreur sont déjà affichés par l'action
                            if (res?.update > 0) {
                                dispatch(OperationAction.fetchHydrobioList(qualitometer, operation))
                                onCloseEditModal()
                                setSelectedHydrobioListId()
                            }
                        })
                }}
                title={i18n.updateEnumeration}
                hydrobioList={selectedHydrobioList}
            />
        </>
    )
}

const Graph = ({
    selectedTaxon = [],
}) => {
    const {
        hydrobioList,
        taxons,
        hydrobioOperation,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        taxons: store.TaxonReducer.taxons,
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
    }), shallowEqual)

    const listFormated = useMemo(() => {
        const listData = hydrobioList.map(({ enumerationA = 0, enumerationB = 0, enumerationC = 0, enumerationC2 = 0, taxon: taxonCode }) => {
            const taxon = taxons.find(({ code }) => taxonCode === code)
            return {
                value: enumerationA + enumerationB + enumerationC + enumerationC2,
                taxon: taxon?.labelWithCode || `<${taxonCode}>`,
                taxonShortLabel: taxon?.latinName || `<${taxonCode}>`,
                taxonCode,
            }
        })
        return orderBy(listData, 'taxonShortLabel')
    }, [hydrobioList, taxons])

    const listFiltered = useMemo(() => {
        return listFormated.filter(l => selectedTaxon.includes(l.taxonCode))
    }, [listFormated, selectedTaxon])

    const options = {
        series: [{
            type: 'bar',
            data: listFiltered,
            itemStyle: {
                normal: {
                    color: 'blue',
                    opacity: 1,
                },
            },
        }],
        xAxis: [{
            type: 'category',
            data: listFiltered.map(({ taxonShortLabel }) => taxonShortLabel),
            axisTick: {
                alignWithLabel: true,
            },
            axisLabel: {
                rotate: 50,
            },
        }],
        yAxis: [{
            type: 'value',
        }],
        tooltip: {
            formatter: ([{ data: { taxonShortLabel, value } }]) => `${taxonShortLabel}</br>${value} ${value > 1 ? i18n.enumerations : i18n.enumeration}`,
            trigger: 'axis',
            axisPointer: {
                show: true,
                type: 'shadow',
            },
        },
        grid: {
            top: '8%',
            left: '3%',
            right: '4%',
            bottom: '80',
            containLabel: true,
            height: 550,
        },
        toolbox: {
            right: '4%',
            top: '2%',
            feature: {
                saveAsImage: {
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: `hydrobio_operation_${getDate(hydrobioOperation.dateStart)}`,
                },
            },
        },
    }
    return (
        <ReactEcharts
            option={options}
            style={{ height: 630 }}
            notMerge
        />
    )
}

Graph.propTypes = {
    selectedTaxon: PropTypes.arrayOf(PropTypes.string),
}

const FilterTab = ({
    selectedTaxon = [],
    setSelectedTaxon = () => {},
}) => {
    const {
        hydrobioList,
        taxons,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [searchValue, setSearchValue] = useState('')
    // const [dataType, setDataType] = useState()
    // const [selection, setSelection] = useState(-1)
    // const [selectionResults, setSelectionResults] = useState([])

    const listFormated = useMemo(() => {
        return hydrobioList.map(list => {
            const taxon = taxons.find(t => t.code === list.taxon)
            const isSelected = selectedTaxon.includes(list.taxon)
            return {
                code: list.taxon,
                name: taxon?.latinName,
                checkbox: {
                    value: (
                        <Icon
                            size='small'
                            icon={isSelected ? 'check_box' : 'check_box_outline_blank'}
                        />
                    ),
                },
                searchValue: normalizeString(`${taxon?.latinName || ''} ${list.taxon}`),
                id: list.taxon,
                isSelected,
                colorLine: isSelected ? THRESHOLD_COLORS_CODES.LIGHT_BLUE : whiteColor,
            }
        })
    }, [hydrobioList, selectedTaxon, taxons])

    const listFiltered = useMemo(() => {
        const searchValueFormat = normalizeString(searchValue)
        return searchValueFormat ? listFormated.filter(l => l.searchValue.includes(searchValueFormat)) : listFormated
    }, [listFormated, searchValue])

    const isAllCheck = useMemo(() => listFiltered.every(l => l.isSelected), [listFiltered])

    const onClickAllCheck = () => {
        const taxonCodes = listFiltered.map(l => l.id)
        const newSelectedTaxon = isAllCheck ? difference(selectedTaxon, taxonCodes) : uniq([...selectedTaxon, ...taxonCodes])
        setSelectedTaxon(newSelectedTaxon)
    }

    return (
        <>
            {/* <Select
                label={i18n.dataTypes}
                options={[]}
                value={dataType}
                onChange={setDataType}
            />
            <SelectionSelect
                onChange={(results, value) => {
                    setSelection(value)
                    setSelectionResults(results)
                }}
            /> */}
            <Input
                title={i18n.search}
                value={searchValue}
                onChange={setSearchValue}
            />
            <NewTable
                rows={listFiltered}
                rowsPerPageOptions={nbPerPageLabel}
                headers={compact([showSandreCode ? 'code' : '', 'name', { key: 'checkbox', value: (<Icon size='small' icon={isAllCheck ? 'check_box_outline_blank' : 'check_box'} />), onClick: onClickAllCheck, sticky: 'right' }])}
                maxHeight='53vh'
                onClickRow={({ isSelected, id }) => {
                    if (isSelected) {
                        setSelectedTaxon(prev => prev.filter(c => c !== id))
                    } else {
                        setSelectedTaxon(prev => [...prev, id])
                    }
                }}
            />
        </>
    )
}

FilterTab.propTypes = {
    selectedTaxon: PropTypes.arrayOf(PropTypes.string),
    setSelectedTaxon: PropTypes.func,
}

const GraphPanel = () => {
    const {
        hydrobioList,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
    }), shallowEqual)

    const [selectedTaxon, setSelectedTaxon] = useState(() => hydrobioList.map(l => l.taxon))

    return (
        <Grid container spacing={1} style={{ padding: '10' }}>
            <Grid size={3}>
                <FilterTab
                    selectedTaxon={selectedTaxon}
                    setSelectedTaxon={setSelectedTaxon}
                />
            </Grid>
            <Grid size={9}>
                <Graph selectedTaxon={selectedTaxon} />
            </Grid>
        </Grid>
    )
}

const HydrobioOperationFloristicList = () => {
    const dispatch = useDispatch()

    const {
        hydrobioList,
        taxons,
        accountUser,
        hydrobioOperation,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        taxons: store.TaxonReducer.taxons,
        accountUser: store.AccountReducer.accountUser,
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
    }), shallowEqual)

    const {
        value: isNewModalOpen,
        setFalse: onCloseNewModal,
        setTrue: onOpenNewModal,
    } = useBoolean(false)

    const { value: editMode } = useBoolean(false)
    const [comment, setComment] = useState(hydrobioOperation.resultComment || '')

    useActions(() => {
        const isUR = hydrobioOperation.support === SUPPORT_MACROPHYTES
        const exportAction = {
            export: () => {
                const sumTotal = sum(hydrobioList.map(({ enumerationA = 0, enumerationB = 0, enumerationC = 0, enumerationC2 = 0 }) => enumerationA + enumerationB + enumerationC + enumerationC2))
                const sumPercentTotal = sum(hydrobioList.map(({ percentageA = 0, percentageB = 0 }) => percentageA + percentageB))
                const data = hydrobioList.map(list => {
                    const {
                        taxon: taxonCode,
                        enumerationA = 0,
                        enumerationB = 0,
                        enumerationC = 0,
                        enumerationC2 = 0,
                    } = list
                    const taxon = taxons.find(t => t.code === taxonCode)
                    const base = {
                        taxonCode,
                        taxon: taxon?.latinName ?? '',
                    }
                    const sumEnumeration = enumerationA + enumerationB + enumerationC + enumerationC2
                    const sumPercent = (list.percentageA ?? 0) + (list.percentageB ?? 0)
                    return isUR ? {
                        ...base,
                        taxonCode,
                        taxon: taxon?.latinName ?? '',
                        UR1: list.percentageA,
                        UR2: list.percentageB,
                        plenty: `${sumPercentTotal !== 0 ? round(100 * sumPercent / sumPercentTotal, 2) : 0} %`,
                    } : {
                        ...base,
                        omnidiaCode: taxon?.omnidiaCode ?? taxon?.omnidiaSynonym ?? taxon?.teraOmnidia ?? taxon?.spomnidia,
                        enumeration: enumerationA + enumerationB + enumerationC + enumerationC2,
                        plenty: `${sumTotal !== 0 ? round(100 * sumEnumeration / sumTotal, 2) : 0} %`,
                    }
                })
                return {
                    data: data.length ? [{ ...data[0], headers: keys(data[0]) }, ...data.slice(1)] : [],
                    exportType: 'xlsx',
                    titleFile: `operation ${getDate(hydrobioOperation.dateStart)}`,
                }
            },
        }
        if (accountUser.consultant === '1') return { ...exportAction }
        if (![4, 10, 11, 13, 27].includes(hydrobioOperation.support)) return { new: onOpenNewModal, ...exportAction }
        return {
            calculateIndexes: {
                qualitometer: hydrobioOperation.qualitometer,
                operations: [hydrobioOperation.id],
                support: hydrobioOperation.support,
            },
            calculateTaxons: {
                operationIds: [hydrobioOperation.id],
                presentTaxons: uniqBy(hydrobioList, 'taxon').map(hl => hl.taxon),
            },
            new: onOpenNewModal,
            ...exportAction,
        }
    }, [hydrobioList, hydrobioOperation])

    return (
        <Card className='no-box-shadow' cardStyle={{ paddingTop: '10' }}>
            <div style={{ margin: '0 10 10 10' }}>
                <Textarea
                    title={i18n.comment}
                    value={comment}
                    onChange={setComment}
                    disabled={!editMode}
                    active={editMode}
                />
            </div>
            <Tabs
                defaultTab={TABLE}
                tabs={[
                    {
                        constant: TABLE,
                        label: i18n.table,
                    },
                    {
                        constant: GRAPH,
                        label: i18n.graph,
                    },
                ]}
            >
                {tab => (
                    <>
                        {tab === TABLE && <TablePanel />}
                        {tab === GRAPH && <GraphPanel />}
                    </>
                )}
            </Tabs>
            <HydrobioFloristicDialog
                isOpen={isNewModalOpen}
                onClose={onCloseNewModal}
                onValidate={(newHydrobioList) => {
                    const {
                        qualitometer,
                        operation,
                    } = newHydrobioList
                    dispatch(HydrobioAction.createHydrobioList(qualitometer, operation, newHydrobioList))
                        .then(res => {
                            if (res?.insert > 0) {
                                dispatch(OperationAction.fetchHydrobioList(qualitometer, operation))
                                onCloseNewModal()
                            }
                        })
                }}
                title={i18n.newEnumeration}
                hydrobioList={{ qualitometer: hydrobioOperation.qualitometer, operation: hydrobioOperation.id, id: -1 }}
            />
        </Card>
    )
}

HydrobioOperationFloristicList.propTypes = {

}

export default HydrobioOperationFloristicList